/* ==========================================================================
   Sway Bar Stiffness Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-sbc]');
  if (!root) return;

  /* Torsional stiffness of a bar goes with the polar second moment of area.
     For a solid bar that is proportional to diameter to the fourth power. For
     a tube it is the outer diameter to the fourth less the inner diameter to
     the fourth, which is why hollow bars save so much weight for so little
     stiffness. */
  function J(dia, type, wall) {
    if (type === 'hollow' && isFinite(wall) && wall > 0 && wall * 2 < dia) {
      var inner = dia - 2 * wall;
      return Math.pow(dia, 4) - Math.pow(inner, 4);
    }
    return Math.pow(dia, 4);
  }

  /* Roll stiffness varies with the inverse square of the lever arm. */
  var ARM_EXPONENT = 2;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.d1) || input.d1 <= 0 || !isFinite(input.d2) || input.d2 <= 0) {
      return { ok: false, errors: ['Enter both bar diameters.'] };
    }
    if (input.t1 === 'hollow' && (!isFinite(input.w1) || input.w1 <= 0 || input.w1 * 2 >= input.d1)) {
      return { ok: false, errors: ['Wall thickness for the current bar has to be less than half its diameter.'] };
    }
    if (input.t2 === 'hollow' && (!isFinite(input.w2) || input.w2 <= 0 || input.w2 * 2 >= input.d2)) {
      return { ok: false, errors: ['Wall thickness for the new bar has to be less than half its diameter.'] };
    }

    var j1 = J(input.d1, input.t1, input.w1);
    var j2 = J(input.d2, input.t2, input.w2);

    var armPct = isFinite(input.armRatio) ? input.armRatio : 0;
    var armFactor = Math.pow(1 / (1 + armPct / 100), ARM_EXPONENT);

    var ratio = (j2 / j1) * armFactor;
    var pct = (ratio - 1) * 100;

    var status;
    if (Math.abs(pct) < 3) {
      status = { key: 'good', label: 'Effectively identical',
                 note: 'Under 3% different. You would not feel this.' };
    } else if (Math.abs(pct) < 20) {
      status = { key: 'good', label: signed(pct, 0) + '% stiffer',
                 note: 'A noticeable but modest change. Good for fine tuning balance.' };
    } else if (Math.abs(pct) < 60) {
      status = { key: 'monitor', label: signed(pct, 0) + '% stiffer',
                 note: 'A substantial change. Expect the balance to shift clearly toward the axle this bar is on.' };
    } else {
      status = { key: 'soon', label: signed(pct, 0) + '% stiffer',
                 note: 'A very large change. On a road car this much bar can make the inside wheel lift and cost grip.' };
    }

    var warnings = [];
    if (pct > 100) {
      warnings.push('More than double the roll stiffness at one end. Adding this much bar to one axle alone will make the car strongly understeer or oversteer depending on which end it is.');
    }

    var rows = [
      { head: 'Current bar' },
      { label: 'Diameter', value: num(input.d1, 1) + ' mm' },
      { label: 'Type', value: input.t1 === 'hollow' ? 'Hollow, ' + num(input.w1, 1) + 'mm wall' : 'Solid' },
      { label: 'Relative stiffness', value: '1.00' },
      { head: 'New bar' },
      { label: 'Diameter', value: num(input.d2, 1) + ' mm' },
      { label: 'Type', value: input.t2 === 'hollow' ? 'Hollow, ' + num(input.w2, 1) + 'mm wall' : 'Solid' },
      { label: 'Relative stiffness', value: num(ratio, 3) },
      { head: 'Change' },
      { label: 'Stiffness', value: signed(pct, 1) + '%' },
      { label: 'Multiplier', value: num(ratio, 2) + 'x' }
    ];
    if (armPct !== 0) {
      rows.push({ label: 'Arm length change', value: signed(armPct, 0) + '%' });
      rows.push({ label: 'From arms alone', value: num(armFactor, 3) + 'x' });
    }
    rows.push({ head: 'What a millimetre is worth' });
    [-1, 1, 2, 3].forEach(function (d) {
      var alt = J(input.d1 + d, input.t1, input.w1) / j1;
      rows.push({ label: num(input.d1 + d, 1) + ' mm solid equivalent', value: signed((alt - 1) * 100, 0) + '%' });
    });

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Stiffness change', value: signed(pct, 1) + '%',
          sub: num(ratio, 2) + ' times the roll stiffness of the current bar.' },
        { label: 'Diameter change', value: signed(input.d2 - input.d1, 1) + ' mm',
          sub: 'Stiffness rises with the fourth power, so a small change goes a long way.' }
      ],
      rows: rows
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "d1",
          "key": "a",
          "type": "number"
      },
      {
          "name": "t1",
          "key": "ta",
          "type": "select"
      },
      {
          "name": "w1",
          "key": "wa",
          "type": "number"
      },
      {
          "name": "d2",
          "key": "b",
          "type": "number"
      },
      {
          "name": "t2",
          "key": "tb",
          "type": "select"
      },
      {
          "name": "w2",
          "key": "wb",
          "type": "number"
      },
      {
          "name": "armRatio",
          "key": "r",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-sbc__figure">'
        + '<div class="pc-sbc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-sbc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-sbc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-sbc__row pc-sbc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-sbc__row">'
        + '<span class="pc-sbc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-sbc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_SBC = { compute: compute };
})();
